import type { Theme } from '@mui/material';
import type { IAnonymousActivities } from 'src/context/activitiesAndBlogs/anonymous_activitiesAndBlogs';

import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router';
import { useRef, useState, useEffect, useCallback } from 'react';

import {
  Box,
  Stack,
  Button,
  Divider,
  TextField,
  Typography,
  IconButton,
  useMediaQuery,
} from '@mui/material';

import {
  setPaginationParent,
  parent_activitiesAndBlogsFunc,
} from 'src/context/activitiesAndBlogs/parent_activitiesAndBlogs';
import {
  setPaginationAnonymous,
  anonymous_activitiesAndBlogsFunc,
} from 'src/context/activitiesAndBlogs/anonymous_activitiesAndBlogs';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';

import { CityFilter } from './filters/CityFilter';
import { DaysFilter } from './filters/DaysFilter';
import { AgesFilter } from './filters/AgesFilter';
import { DateFilter } from './filters/DateFilter';
import { PriceFilter } from './filters/PriceFilter';
import { FormatFilter } from './filters/FormatFilter';
import { ParkingFilter } from './filters/ParkingFilter';
import { CategoryFilter } from './filters/CategoryFilter';

// ----------------------------------------------------------------------

export function ProductFiltersMenu(props: {
  sort?: string;
  setCategoryName: (name: string) => void;
  showMap?: boolean;
}) {
  const downSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const accessTokenJSON = localStorage.getItem('auth') as string;
  const accessToken = JSON.parse(accessTokenJSON)?.accessToken ?? null;

  const dispatch = useDispatch();
  const debounceTimer = useRef<NodeJS.Timeout>();
  const activitiesAndBlogs = useSelector((state: any) =>
    accessToken ? state.parent_activitiesAndBlogs : state.anonymous_activitiesAndBlogs
  );
  const { page } = useSelector((state: any) =>
    accessToken ? state.parent_activitiesAndBlogs : state.anonymous_activitiesAndBlogs
  );
  // router dom ile params çek
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const search = searchParams.get('arama') || null;
  const category = searchParams.get('kategori') || null;
  const city = searchParams.get('sehir') || null;
  const district = searchParams.get('ilce') || null;
  const days = searchParams.get('gunler') || null;
  const ageInterval = searchParams.get('yas') || null;
  const format = searchParams.get('format') || null;
  const fee = searchParams.get('ucret') || null;
  const minPrice = searchParams.get('min') || null;
  const maxPrice = searchParams.get('max') || null;
  const minDate = searchParams.get('minDate') || null;
  const maxDate = searchParams.get('maxDate') || null;
  const parking = searchParams.get('park') || null;
  const activityBlogFilter = searchParams.get('activityBlogFilter') || null;

  const [filters, setFilters] = useState<IAnonymousActivities>({
    sort: props.sort,
    listing: {
      title: null,
      ageInterval: null,
      format: null,
      category: null,
    },
    location: {
      district: null,
      city: null,
      parking: null,
    },
    schedule: {
      days: null,
      dateInterval: {
        from: null,
        to: null,
      },
    },
    fee: null,
    price: {
      from: null,
      to: null,
    },
    fromQuota: null,
    toQuota: null,
    byCoordinates: null,
    long: null,
    lat: null,
    finished: null,
    page: 1,
    pageSize: props.showMap ? 500 : 15,
    activityBlogFilterAdditionalDetails: null,
  });

  useEffect(() => {
    if (filters?.listing?.category || category) {
      props.setCategoryName(
        filters?.listing?.category ? filters?.listing?.category : (category ?? 'Aktiviteler')
      );
    }
  }, [filters?.listing?.category, category]);

  // showMap değiştiğinde pageSize'ı güncelle
  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      pageSize: props.showMap ? 500 : 15,
    }));
  }, [props.showMap]);

  useEffect(() => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      sort: props.sort,
      location: {
        ...prevFilters.location,
        city,
        district,
        parking: parking || null,
      },
      listing: {
        ...prevFilters.listing,
        title: search,
        category,
        ageInterval: ageInterval ? ageInterval.split(',').map(Number) : null,
        format: format || null,
      },
      schedule: {
        ...prevFilters.schedule,
        days: days ? days.split(',').map(Number) : null,
        // dateInterval: {
        //   from: minDate ? dayjs(minDate).format('YYYY-MM-DD') : null,
        //   to:    maxDate ? dayjs(maxDate).format('YYYY-MM-DD') : null,
        // }, TODO: fix date filter
      },
      fee: fee || null,
      price: {
        from: minPrice ? parseInt(minPrice, 10) : null,
        to: maxPrice ? parseInt(maxPrice, 10) : null,
      },
      activityBlogFilterAdditionalDetails: activityBlogFilter || null,
    }));
  }, [
    city,
    district,
    search,
    category,
    days,
    ageInterval,
    format,
    fee,
    minPrice,
    maxPrice,
    minDate,
    maxDate,
    parking,
    activityBlogFilter,
    props.sort,
  ]);

  useEffect(() => {
    if (dispatch && !accessToken) {
      dispatch(
        anonymous_activitiesAndBlogsFunc({
          ...filters,
          page,
          listing: {
            ...filters?.listing,
            title: filters?.listing?.title ? filters?.listing?.title : search ? search : null,
            category: filters?.listing?.category
              ? filters?.listing?.category
              : category
                ? category
                : null,
            ageInterval: filters?.listing?.ageInterval
              ? filters?.listing?.ageInterval
              : ageInterval
                ? ageInterval.split(',').map(Number)
                : null,
            format: filters?.listing?.format ? filters?.listing?.format : format ? format : null,
          },
          location: {
            ...filters?.location,
            city: filters?.location.city ? filters.location.city : city ? city : null,
            district: filters?.location.district
              ? filters.location.district
              : district
                ? district
                : null,
            parking: filters?.location.parking
              ? filters.location.parking
              : parking
                ? parking
                : null,
          },
          schedule: {
            ...filters?.schedule,
            days: filters?.schedule.days
              ? filters.schedule.days
              : days
                ? days.split(',').map(Number)
                : null,
            dateInterval: {
              from:
                filters?.schedule.dateInterval.from !== null
                  ? filters.schedule.dateInterval.from
                  : minDate
                    ? dayjs(minDate).format('YYYY-MM-DD')
                    : null,
              to:
                filters?.schedule.dateInterval.to !== null
                  ? filters.schedule.dateInterval.to
                  : maxDate
                    ? dayjs(maxDate).format('YYYY-MM-DD')
                    : null,
            },
          },
          fee: filters?.fee ? filters.fee : fee ? fee : null,
          price: {
            from:
              filters.price.from !== null
                ? filters.price.from
                : minPrice
                  ? parseInt(minPrice, 10)
                  : null,
            to:
              filters.price.to !== null
                ? filters.price.to
                : maxPrice
                  ? parseInt(maxPrice, 10)
                  : null,
          },
          activityBlogFilterAdditionalDetails: filters?.activityBlogFilterAdditionalDetails
            ? filters.activityBlogFilterAdditionalDetails
            : activityBlogFilter
              ? activityBlogFilter
              : null,
        })
      );
    } else if (dispatch && accessToken) {
      dispatch(
        parent_activitiesAndBlogsFunc({
          ...filters,
          page,
          listing: {
            ...filters?.listing,
            title: filters?.listing?.title ? filters?.listing?.title : search ? search : null,
            category: filters?.listing?.category
              ? filters?.listing?.category
              : category
                ? category
                : null,
            ageInterval: filters?.listing?.ageInterval
              ? filters?.listing?.ageInterval
              : ageInterval
                ? ageInterval.split(',').map(Number)
                : null,
            format: filters?.listing?.format ? filters?.listing?.format : format ? format : null,
          },
          location: {
            ...filters?.location,
            city: filters?.location.city ? filters.location.city : city ? city : null,
            district: filters?.location.district
              ? filters.location.district
              : district
                ? district
                : null,
            parking: filters?.location.parking
              ? filters.location.parking
              : parking
                ? parking
                : null,
          },
          schedule: {
            ...filters?.schedule,
            days: filters?.schedule.days
              ? filters.schedule.days
              : days
                ? days.split(',').map(Number)
                : null,
            dateInterval: {
              from:
                filters?.schedule.dateInterval.from !== null
                  ? filters.schedule.dateInterval.from
                  : minDate
                    ? dayjs(minDate).format('YYYY-MM-DD')
                    : null,
              to:
                filters?.schedule.dateInterval.to !== null
                  ? filters.schedule.dateInterval.to
                  : maxDate
                    ? dayjs(maxDate).format('YYYY-MM-DD')
                    : null,
            },
          },
          fee: filters?.fee ? filters.fee : fee ? fee : null,
          price: {
            from:
              filters.price.from !== null
                ? filters.price.from
                : minPrice
                  ? parseInt(minPrice, 10)
                  : null,
            to:
              filters.price.to !== null
                ? filters.price.to
                : maxPrice
                  ? parseInt(maxPrice, 10)
                  : null,
          },
          activityBlogFilterAdditionalDetails: filters?.activityBlogFilterAdditionalDetails
            ? filters.activityBlogFilterAdditionalDetails
            : activityBlogFilter
              ? activityBlogFilter
              : null,
        })
      );
    }
  }, [
    dispatch,
    filters.listing.title,
    filters.listing.category,
    filters.listing.ageInterval,
    filters.listing.format,
    filters.location.city,
    filters.location.district,
    filters.location.parking,
    filters.schedule.days,
    filters.schedule.dateInterval.from,
    filters.schedule.dateInterval.to,
    filters.price.from,
    filters.price.to,
    filters.fee,
    filters.activityBlogFilterAdditionalDetails,
    filters.pageSize,
    props.sort,
    props.showMap,
    page,
    accessToken,
  ]);

  const handleSearch = () => {
    if (accessToken) {
      dispatch(setPaginationParent(1));
    } else {
      dispatch(setPaginationAnonymous(1));
    }

    // Sonra arama yap
    setTimeout(() => {
      if (accessToken) {
        dispatch(parent_activitiesAndBlogsFunc({ ...filters, page: 1 }));
      } else {
        dispatch(anonymous_activitiesAndBlogsFunc({ ...filters, page: 1 }));
      }
    }, 0);
  };

  // Debounce edilmiş arama fonksiyonu
  const debouncedSearch = useCallback(
    (searchValue?: string) => {
      const titleToUse = searchValue !== undefined ? searchValue : filters.listing.title;

      // Önceki timer'ı temizle
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // 500ms sonra arama yap
      debounceTimer.current = setTimeout(() => {
        // Güncel filters'ı kullan ama title için parametre değerini kullan
        const updatedFilters = {
          ...filters,
          listing: {
            ...filters.listing,
            title: titleToUse,
          },
        };

        // Pagination sıfırla ve arama yap
        if (accessToken) {
          dispatch(setPaginationParent(1));
          dispatch(parent_activitiesAndBlogsFunc({ ...updatedFilters, page: 1 }));
        } else {
          dispatch(setPaginationAnonymous(1));
          dispatch(anonymous_activitiesAndBlogsFunc({ ...updatedFilters, page: 1 }));
        }
      }, 500);
    },
    [filters, accessToken, dispatch]
  );

  const renderHead = () => (
    <Box
      sx={{
        py: 2,
        px: 4,
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'primary.main',
      }}
    >
      <Typography variant="h6" fontWeight={400} sx={{ color: 'common.white' }}>
        <strong>
          {accessToken
            ? (activitiesAndBlogs?.parent_activitiesAndBlogsData?.count ?? 0)
            : (activitiesAndBlogs?.anonymous_activitiesAndBlogsData?.count ?? 0)}
        </strong>{' '}
        aktivite listeleniyor.
      </Typography>
    </Box>
  );

  const renderSearch = () => (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, mt: 2, alignItems: 'center' }}>
        <TextField
          id="input-with-sx"
          label="Arama Yap"
          variant="outlined"
          type="text"
          value={filters.listing.title || ''}
          size="small"
          onChange={(e) => {
            const searchValue = e.target.value ?? '';
            setFilters({
              ...filters,
              listing: { ...filters?.listing, title: searchValue },
            });
            // Debounce edilmiş arama yap (boş string dahil) - yeni değeri parametre olarak geç
            debouncedSearch(searchValue);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: '100px',
            },
          }}
        />
        <IconButton onClick={handleSearch} sx={{ alignSelf: 'flex-end', mt: 1 }}>
          <Iconify icon="mdi:magnify" color="primary" width={24} height={24} />
        </IconButton>
      </Box>
      <Divider />
    </>
  );

  const renderActions = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Button
        variant="contained"
        color="primary"
        size="large"
        onClick={() => {
          // URL parametrelerini temizle
          navigate(window.location.pathname, { replace: true });

          // Pagination'ı sıfırla
          if (accessToken) {
            dispatch(setPaginationParent(1));
          } else {
            dispatch(setPaginationAnonymous(1));
          }

          // Filtreleri resetle
          setFilters({
            listing: {
              title: null,
              ageInterval: null,
              format: null,
              category: null,
            },
            location: {
              district: null,
              city: null,
              parking: null,
            },
            schedule: {
              days: null,
              dateInterval: {
                from: null,
                to: null,
              },
            },
            fee: null,
            price: {
              from: null,
              to: null,
            },
            fromQuota: null,
            toQuota: null,
            byCoordinates: null,
            long: null,
            lat: null,
            finished: null,
            page: 1,
            pageSize: props.showMap ? 500 : 15,
            activityBlogFilterAdditionalDetails: '',
          });
        }}
      >
        Filtreyi Resetle
      </Button>
      {/* <Button variant="contained" color="primary" size="large" onClick={handleSearch}>
        Uygula
      </Button> */}
    </Box>
  );

  return (
    <Box
      sx={{
        width: downSm ? '100%' : 280,
        height: 'fit-content',
        overflow: 'hidden',
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        mt: 4,
      }}
    >
      {renderHead()}

      <Scrollbar sx={{ px: 2.5, py: 3 }}>
        <Stack spacing={3}>
          {renderSearch()}
          <CategoryFilter filters={filters} setFilters={setFilters} />
          <Divider />
          <CityFilter filters={filters} setFilters={setFilters} />
          <Divider />
          <DaysFilter filters={filters} setFilters={setFilters} />
          <Divider />
          <AgesFilter filters={filters} setFilters={setFilters} />
          <Divider />
          <FormatFilter filters={filters} setFilters={setFilters} />
          <Divider />
          <DateFilter filters={filters} setFilters={setFilters} />
          <Divider />
          <PriceFilter filters={filters} setFilters={setFilters} />
          {/* <Divider />
          <PriceRangeFilter filters={filters} setFilters={setFilters} /> */}
          <Divider />
          <ParkingFilter filters={filters} setFilters={setFilters} />
          {renderActions()}
        </Stack>
      </Scrollbar>
    </Box>
  );
}
